import axiosInstance from './axiosConfig';

/**
 * UGC動画広告（UGC Video Ad）API クライアント。
 *
 * プロジェクト = 構成案（シーン配列: セリフ/ナレーション + 字コンテ）+ シーンごとの
 * ストーリーボード（開始フレーム静止画）/クリップ（動画）+ 合成済み動画。
 * 歌広告（songAdService.ts）の契約を踏襲し、生成ジョブの進捗は
 * GET /ugc-ads/{id} のポーリングだけで追跡する（シーンごとに個別ポーリングしない）。
 *
 * 歌広告との違い:
 * - 楽曲（歌詞/曲）が無い。②は「構成案」（セリフ+字コンテのテキスト）で、フィックスすると
 *   シーンがそのまま確定する（歌広告の 歌詞→シーン分割 の2段階を1段階に統合）
 * - 出演者（cast_gender / cast_age）と構成タイプ（format_type）を持つ
 * - アスペクト比は 9:16 固定（TikTok/Reels/Shorts向け。フォームでは選択させない）
 * - トーク中心シーンのクリップは人物が実際に話す映像（音声込み）を生成する想定のため、
 *   プレビュー合成（UgcAdComposition）ではクリップ音声をミュートしない
 */

export type UgcAdProjectStatus =
  | 'draft'
  | 'script_generating'
  | 'script_ready'
  | 'composing'
  | 'completed'
  | 'compose_failed';

/** シーン段階の状態（採用済み成果物とジョブ状態から導出される。song-ad の SceneStageStatus と同一） */
export type SceneStageStatus = 'none' | 'pending' | 'processing' | 'completed' | 'failed';

/** 9:16 固定（他比率は当面提供しない。型は文字列literalで固定し、契約ごと縛る） */
export type UgcAspectRatio = '9:16';

/**
 * ショート動画の構成タイプ。
 * - talk: 出演者がカメラに向かって話すのが中心（体験談・レビュー系）
 * - product: 商品カット・使用シーンが中心。セリフはナレーション/テロップで補う
 * - balance: トークと商品カットを交互に組み合わせる（迷ったらこれ・デフォルト）
 */
export type UgcFormatType = 'talk' | 'product' | 'balance';

export type CastGender = 'auto' | 'female' | 'male';

/** 出演者の年代。'auto'＝商品・訴求からAIが選ぶ */
export type CastAge = 'auto' | '10s' | '20s' | '30s' | '40s' | '50s' | '60s';

/** 動画の長さ（秒）。短尺UGCの主戦場は15〜30秒 */
export type UgcTargetSeconds = 15 | 30 | 45 | 60;

export type UgcAdFinalVideoKind = 'final' | 'still_preview';

/** 書き出しに足りない必須素材1件 */
export interface UgcAdMissingAsset {
  type: 'storyboard' | 'clip' | 'captions' | 'logo';
  scene_id: string | null;
  scene_number: number | null;
  label: string;
}

export type MotionMode = 'clip' | 'still' | 'skip';
export type TelopPosition = 'top' | 'middle' | 'bottom';
export type TelopSize = 'sm' | 'md' | 'lg';
export type CaptionStyle = 'simple' | 'pop' | 'impact' | 'premium';

export interface UgcAdAdvancedSettings {
  required_words: string[];
  banned_words: string[];
  cta: string | null;
  /** CTAロゴの明示指定。null＝未指定（backendが brand_logo → product_image_keys[0] の順で自動解決） */
  logo_image_key: string | null;
  /**
   * テロップと音声の合わせ方。
   * - `proportional`（既定）: クリップの実尺にシーン枠を合わせ、その中でセリフを按分する
   * - `whisper`: クリップ音声を文字起こしして、実際に話している区間へテロップを寄せる
   *   （精度は上がるがクリップ本数ぶんの時間と費用がかかる）
   */
  caption_sync: UgcAdCaptionSync;
}

export type UgcAdCaptionSync = 'proportional' | 'whisper';

/** 広告媒体。既存プロジェクトは null（作成時に選ばなかった／機能追加前） */
export type UgcAdPlatform = 'instagram_reels' | 'tiktok' | 'youtube_shorts' | 'x' | 'line' | 'other';

/** 広告目的 */
export type UgcAdObjective = 'awareness' | 'consideration' | 'conversion' | 'app_install';

/** 媒体×目的から返る推奨値（GET /ugc-ads/recommendations） */
export interface UgcAdRecommendation {
  format_type: UgcFormatType;
  target_seconds: UgcTargetSeconds;
  aspect_ratio: UgcAspectRatio;
  /** 「Instagram Reelsで認知獲得なら30秒・商品フォーカスがおすすめです」 */
  reason: string;
  aspect_note: string;
  platforms: { value: UgcAdPlatform; label: string }[];
  objectives: { value: UgcAdObjective; label: string }[];
}

/** 末尾CTAカードの解決済み内容（song-ad の SongAdCtaSettings と同一契約） */
export interface UgcAdCtaSettings {
  enabled: boolean;
  text: string | null;
  logo_url: string | null;
}

/**
 * 出演者リファレンスの候補1件（POST /ugc-ads/{id}/cast-candidates が生成）。
 * `s3_key` を PATCH の `cast_image_key` に渡すとその人物で確定される。
 */
export interface UgcAdCastCandidate {
  s3_key: string;
  url: string;
}

/**
 * ③キャラクターの「特徴」。画面で編集して再生成できる。
 *
 * 生成に埋め込まれる完成形のロック文は backend の内部データで、ここには含まれない
 * （長文で編集対象にならないため画面には出さない）。
 */
export interface UgcAdCharacterFeatures {
  /** 顔立ち・髪・肌など。同じ人物を何度も描くための記述 */
  appearance: string[];
  /** 衣装。全カットで同じ服を着る前提 */
  outfit: string[];
  /** 人物像の一言まとめ */
  persona: string | null;
  /** 声の指定。発話音声をブレさせないために動画生成へ渡す */
  voice: string[];
}

/** ③キャラクターの状態。`status` が pending/processing の間はポーリング対象 */
export interface UgcAdCharacter {
  image_url: string | null;
  features: UgcAdCharacterFeatures;
  /**
   * 利用者が画面で指定した特徴（未指定なら null）。
   *
   * `features` は「実際に描かれた人物の特徴」で、生成完了時に Vision の読み取りが混ざる。
   * こちらは利用者の意図そのものなので、生成中の適用条件表示と、生成失敗時に
   * フォームを巻き戻さないための正本として使う。
   */
  requested_features: UgcAdCharacterFeatures | null;
  status: SceneStageStatus;
  error_message: string | null;
  /** 人物固定テキストが確定していて、以降の生成に使える状態か */
  locked: boolean;
}

/** テロップ1件。詳細編集タイムラインの正本データ（song-ad CaptionSegment と同一契約） */
export interface CaptionSegment {
  id: string;
  text: string;
  start: number;
  end: number;
  style: CaptionStyle;
  position: TelopPosition;
  size: TelopSize;
}

/** 詳細編集タイムラインの正本JSON（ugc_ad_projects.timeline） */
export interface UgcAdTimeline {
  captions: CaptionSegment[];
  scene_order: string[];
  motion: Record<string, { mode: MotionMode; prompt: string | null }>;
  /**
   * ユーザーが詳細編集で指定したシーンの尺（秒）。シーンID → 秒数。
   *
   * 未指定のシーンは従来どおり `duration_sec` 比で総尺を按分する。1つでも指定があると
   * 動画全体の長さは「各シーンの尺の合計」になる（総尺可変）。
   * シーンは常に連続配置されるため開始位置は持たない（前のシーンの終端から導出する）。
   */
  scene_durations?: Record<string, number>;
  updated_client_at: string;
}

export interface UgcAdSceneStoryboard {
  status: SceneStageStatus;
  job_id: string | null;
  error_message: string | null;
  image: { id: number; url: string; s3_key: string } | null;
}

export interface UgcAdSceneClip {
  status: SceneStageStatus;
  job_id: string | null;
  progress_percentage: number | null;
  progress_step: string | null;
  error_message: string | null;
  video: { id: number; url: string; duration_seconds: number } | null;
}

/**
 * 構成上の役割ラベル（フック/共感/商品紹介/実演/CTA など）。
 * backendのLLMが構成案生成時に自由記述で付ける表示用ラベルなので、enumで縛らない。
 */
export interface UgcAdScene {
  id: string;
  display_order: number;
  /** 構成上の役割（例: 'フック'）。表示用。null可 */
  beat: string | null;
  /** セリフ/ナレーション（話す内容そのもの。テロップの初期値にも使われる） */
  dialogue: string;
  /** 字コンテ（画面に映るものの説明。ストーリーボード生成のもとになる） */
  direction: string;
  /** このシーンの推定尺（秒）。backendが構成案生成時に配分する。シーン枠の按分にも使う */
  duration_sec: number;
  storyboard_prompt: string | null;
  clip_prompt: string | null;
  motion_mode: MotionMode;
  motion_prompt: string | null;
  telop_position: TelopPosition;
  telop_size: TelopSize;
  storyboard: UgcAdSceneStoryboard;
  clip: UgcAdSceneClip;
}

/**
 * 一覧・詳細どちらのレスポンスも表す共通型。
 * `scenes` は詳細取得（GET /ugc-ads/{id}）のときのみ含まれる。
 */
export interface UgcAdProject {
  id: string;
  title: string;
  status: UgcAdProjectStatus;
  product_id: number | null;
  product_image_keys: string[];
  aspect_ratio: UgcAspectRatio;
  format_type: UgcFormatType;
  /** 広告媒体。推奨値の算出に使う。既存プロジェクトは null */
  ad_platform: UgcAdPlatform | null;
  /** 広告目的。推奨値の算出に使う。既存プロジェクトは null */
  ad_objective: UgcAdObjective | null;
  cast_gender: CastGender;
  cast_age: CastAge;
  /**
   * 出演者リファレンス画像のS3キー（顔の一貫性アンカー）。null＝おまかせ
   * （構成案フィックス時にbackendが候補から自動で1人確定する）。
   * 確定後は全シーンのストーリーボード生成に参照画像として必ず渡される。
   */
  cast_image_key: string | null;
  /** 確定済み出演者リファレンスの解決済みURL（表示用。未確定時は null） */
  cast_image_url: string | null;
  /** 直近に生成した出演者候補（未生成時は空配列。再訪時も表示できるようbackendが保持する） */
  cast_candidates: UgcAdCastCandidate[];
  /** 出演者候補生成ジョブの状態。pending/processing の間はポーリング対象 */
  cast_candidates_status: SceneStageStatus;
  cast_candidates_error_message: string | null;
  /** ③キャラクター（人物のテキストロック）。構成案の確定後にここで人物を決める */
  character: UgcAdCharacter;
  target_seconds: number;
  /** 追加の自由コメント（訴求・トーン・シチュエーションの指示など） */
  free_text: string | null;
  advanced_settings: UgcAdAdvancedSettings;
  /** 構成案フィックス日時。null＝未フィックス（②構成案の done 判定に使う） */
  script_fixed_at: string | null;
  /** 末尾CTAカードの自動付与ON/OFF。既定true。`cta.enabled` と同値（互換のため残す） */
  cta_enabled: boolean;
  /** 末尾CTAカードの解決済み内容 */
  cta: UgcAdCtaSettings;
  /** 詳細編集タイムラインの正本。captions/init 前は null */
  timeline: UgcAdTimeline | null;
  final_video_url: string | null;
  /**
   * 書き出した成果物の種別。
   * - `final`: 全必須素材が揃った完成動画
   * - `still_preview`: クリップが揃わないまま利用者が明示的に選んだ静止画版
   *
   * 失敗版と完成版を同じラベルで扱わないための区別（UGC-08）。
   */
  final_video_kind: UgcAdFinalVideoKind;
  /** 書き出しに足りない必須素材。詳細取得（GET /ugc-ads/{id}）にだけ含まれる */
  missing_assets?: UgcAdMissingAsset[];
  compose_error_message: string | null;
  composed_at: string | null;
  thumbnail_url?: string | null;
  scene_count?: number;
  storyboard_done_count?: number;
  clip_done_count?: number;
  scenes?: UgcAdScene[];
  created_at: string;
  updated_at: string;
}

export interface CreateUgcAdProjectParams {
  /** 任意入力。未送信/空ならサーバー側で商品名＋「UGC動画広告」＋日付から自動命名される */
  title?: string;
  product_id?: number;
  product_image_keys?: string[];
  format_type?: UgcFormatType;
  /** 広告媒体。構成タイプ・尺の推奨値の根拠として保存する */
  ad_platform?: UgcAdPlatform;
  /** 広告目的 */
  ad_objective?: UgcAdObjective;
  cast_gender?: CastGender;
  cast_age?: CastAge;
  target_seconds?: UgcTargetSeconds;
  free_text?: string;
  advanced_settings?: UgcAdAdvancedSettings;
}

/** PATCH /ugc-ads/{id}。①情報登録タブの保存もこれで送る */
export interface UpdateUgcAdProjectParams {
  title?: string;
  product_id?: number | null;
  product_image_keys?: string[];
  format_type?: UgcFormatType;
  cast_gender?: CastGender;
  cast_age?: CastAge;
  /** 出演者リファレンス画像のS3キー。null送信＝おまかせに戻す */
  cast_image_key?: string | null;
  target_seconds?: UgcTargetSeconds;
  free_text?: string;
  advanced_settings?: UgcAdAdvancedSettings;
  /** 末尾CTAカードの自動付与ON/OFF。⑤プレビューのCTAトグルから送る */
  cta_enabled?: boolean;
}

/**
 * シーン一括更新（PUT /ugc-ads/{id}/scenes）の1要素。
 * 送った配列の順 = 新display_order。id なし = 新規シーン。送られなかった既存id = 削除。
 * （song-ad の PUT /scenes と同じB4仕様）
 */
export interface UpdateUgcAdSceneInput {
  id?: string;
  beat?: string | null;
  dialogue?: string;
  direction?: string;
  motion_mode?: MotionMode;
  motion_prompt?: string | null;
  telop_position?: TelopPosition;
  telop_size?: TelopSize;
}

/**
 * ローカル画像アップロード。base64（data URLプレフィックス無し）を送りS3キーを受け取る。
 * project_id に依存しない（作成前の作成パネルからも呼べる。song-ad B6 と同契約）。
 */
const UPLOAD_IMAGE_PATH = '/ugc-ads/upload-image';

/** 画像ファイルをbase64（data URLプレフィックス無し）に変換する */
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(new Error('画像の読み込みに失敗しました'));
    reader.readAsDataURL(file);
  });

const extractErrorMessage = (error: any): string =>
  error?.response?.data?.message || error?.message || 'エラーが発生しました';

/** 402 credit_insufficient エラーかどうかを判定する（グローバルリスナーは存在しないためページ側で使う） */
const isCreditInsufficientError = (error: any): boolean =>
  error?.response?.status === 402 || error?.response?.data?.error === 'credit_insufficient';

const ugcAdService = {
  async getProjects(): Promise<UgcAdProject[]> {
    const response = await axiosInstance.get('/ugc-ads');
    return response.data.data.projects;
  },

  async createProject(params: CreateUgcAdProjectParams): Promise<UgcAdProject> {
    const response = await axiosInstance.post('/ugc-ads', params);
    return response.data.data.project;
  },

  /** ローカル画像をアップロードしS3キーを取得する */
  async uploadImage(file: File): Promise<{ s3Key: string }> {
    const base64 = await fileToBase64(file);
    const response = await axiosInstance.post(UPLOAD_IMAGE_PATH, { image_data: base64 });
    return { s3Key: response.data.data.s3_key };
  },

  async getProject(id: string): Promise<UgcAdProject> {
    const response = await axiosInstance.get(`/ugc-ads/${id}`);
    return response.data.data.project;
  },

  async updateProject(id: string, params: UpdateUgcAdProjectParams): Promise<UgcAdProject> {
    const response = await axiosInstance.patch(`/ugc-ads/${id}`, params);
    return response.data.data.project;
  },

  async deleteProject(id: string): Promise<void> {
    await axiosInstance.delete(`/ugc-ads/${id}`);
  },

  /**
   * 構成案生成（同期LLM、〜30s）。シーン配列（beat/dialogue/direction/duration_sec）を
   * 一括生成して返す。instruction 指定でAI修正チップ・自由入力からの再生成に使う。
   * 既にシーンがある状態で呼ぶと全シーンを作り直す（下流の絵コンテ/クリップは失効）。
   */
  async generateScript(id: string, instruction?: string): Promise<UgcAdProject> {
    const response = await axiosInstance.post(`/ugc-ads/${id}/generate-script`, {
      ...(instruction ? { instruction } : {}),
    });
    return response.data.data.project;
  },

  /**
   * 構成案フィックス。現在のシーン内容を確定し script_fixed_at を立てる
   * （③ストーリーボード生成の前提条件。歌広告の歌詞フィックスに相当）。
   */
  async fixScript(id: string): Promise<UgcAdProject> {
    const response = await axiosInstance.post(`/ugc-ads/${id}/fix-script`);
    return response.data.data.project;
  },

  /**
   * 出演者リファレンス候補の生成（非同期）。cast_gender/cast_age/free_text/商品情報から
   * 候補ポートレートを4枚程度生成し、完了すると `cast_candidates` に入る
   * （進捗は `cast_candidates_status` をGET詳細ポーリングで追跡）。
   * credit.check:image_generation。生成中の重複発行は409。
   */
  async generateCastCandidates(id: string): Promise<UgcAdProject> {
    const response = await axiosInstance.post(`/ugc-ads/${id}/cast-candidates`);
    return response.data.data.project;
  },

  /**
   * ③キャラクターの生成・再生成（非同期）。
   *
   * 人物画像を1枚作り、その絵を読み取って「同じ人物を描き続けるためのテキスト」を確定する。
   * `features` を渡すとその内容で作り直す（省略時は商品・出演者指定から初回の案を起こす）。
   * credit.check:image_generation。生成中の重複発行は409。
   */
  async generateCharacter(id: string, features?: UgcAdCharacterFeatures): Promise<UgcAdProject> {
    const response = await axiosInstance.post(`/ugc-ads/${id}/character`, features ? { features } : {});
    return response.data.data.project;
  },

  /** 絵コンテ生成（非同期）。credit.check:image_generation。重複発行は409 */
  async generateStoryboard(projectId: string, sceneId: string): Promise<void> {
    await axiosInstance.post(`/ugc-ads/${projectId}/scenes/${sceneId}/storyboard`);
  },

  /** クリップ生成（非同期）。credit.check:video_generation。絵コンテ未生成422、同時実行超過429 */
  async generateClip(projectId: string, sceneId: string): Promise<void> {
    await axiosInstance.post(`/ugc-ads/${projectId}/scenes/${sceneId}/clip`);
  },

  /**
   * シーン一括更新（並び替え・追加・削除・セリフ/字コンテ・motion_mode/motion_prompt・
   * テロップ位置/サイズ）。送った配列の順がそのまま新しい display_order になる。
   */
  async updateScenes(id: string, scenes: UpdateUgcAdSceneInput[]): Promise<UgcAdProject> {
    const response = await axiosInstance.put(`/ugc-ads/${id}/scenes`, { scenes });
    return response.data.data.project;
  },

  /** シーン単位のAI修正（非同期job発行、202） */
  async aiEditScene(projectId: string, sceneId: string, instruction: string): Promise<void> {
    await axiosInstance.post(`/ugc-ads/${projectId}/scenes/${sceneId}/ai-edit`, { instruction });
  },

  /** モーション設定をAIにおまかせ（フック・商品・CTA優先で全シーンへ motion_mode/motion_prompt を提案・適用） */
  async motionAuto(projectId: string): Promise<UgcAdProject> {
    const response = await axiosInstance.post(`/ugc-ads/${projectId}/motion/auto`);
    return response.data.data.project;
  },

  /** シーン画像を商品画像（s3_key）に差し替える */
  async replaceSceneImage(projectId: string, sceneId: string, s3Key: string): Promise<UgcAdProject> {
    const response = await axiosInstance.post(`/ugc-ads/${projectId}/scenes/${sceneId}/replace-image`, {
      s3_key: s3Key,
    });
    return response.data.data.project;
  },

  /**
   * テロップ初期生成（各シーンの dialogue と duration_sec から CaptionSegment を生成し timeline に保存）。
   * script_fixed 必須（422）。既に timeline.captions があれば 409（force=true で上書き）。
   */
  async initCaptions(projectId: string, force = false): Promise<UgcAdProject> {
    const response = await axiosInstance.post(
      `/ugc-ads/${projectId}/captions/init${force ? '?force=1' : ''}`
    );
    return response.data.data.project;
  },

  /** 詳細編集タイムラインの自動保存（PATCH。楽観ロックなし・last-write-wins） */
  async updateTimeline(projectId: string, timeline: UgcAdTimeline): Promise<UgcAdProject> {
    const response = await axiosInstance.patch(`/ugc-ads/${projectId}/timeline`, { timeline });
    return response.data.data.project;
  },

  /** 合成開始（非同期job）。構成案フィックス+絵コンテ全生成必須(422)、composing中は409 */
  /**
   * 書き出しを開始する。
   *
   * 必須素材（絵コンテ・クリップ・テロップ・CTAロゴ）が欠けていると 422 で止まる。
   * クリップだけが足りない場合に限り、`allowStill` を明示したときだけ
   * 「静止画版の仮プレビュー」として書き出せる（UGC-08）。
   */
  /**
   * 媒体・目的から構成タイプと尺の推奨値を取る（UGC-01）。
   * プロジェクト作成前に呼べる。未指定でも既定の推奨が返る。
   */
  async getRecommendations(platform?: UgcAdPlatform, objective?: UgcAdObjective): Promise<UgcAdRecommendation> {
    const response = await axiosInstance.get('/ugc-ads/recommendations', {
      params: { platform, objective },
    });

    return response.data.data;
  },

  /**
   * CTA文言の候補をAIで作る（UGC-07）。必ず1件以上返る（LLMが落ちても定番候補）。
   */
  async getCtaSuggestions(id: string): Promise<string[]> {
    const response = await axiosInstance.post(`/ugc-ads/${id}/cta-suggestions`);

    return response.data.data.suggestions ?? [];
  },

  async compose(id: string, allowStill = false): Promise<void> {
    await axiosInstance.post(`/ugc-ads/${id}/compose`, allowStill ? { allow_still: true } : {});
  },

  extractErrorMessage,
  isCreditInsufficientError,
};

export default ugcAdService;
